import React, { useEffect, useMemo, useState } from 'react';
import { Calendar as RBCalendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import { es } from 'date-fns/locale';
import { RRule, RRuleSet, rrulestr } from 'rrule';
import { Search, CalendarDays, Trash2, Plus, Play, Mail, PhoneOutgoing, X as XIcon, Repeat, History, List as ListIcon } from 'lucide-react';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import { useAuth } from '../context/AuthContext';
import useUserPreference from '../hooks/useUserPreference';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import DataTable from '../components/DataTable';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import './calendar.css';

const locales = { es };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek: (d) => startOfWeek(d, { weekStartsOn: 1 }), getDay, locales });
const DnDCalendar = withDragAndDrop(RBCalendar);

const formatDateLong = (d) => d ? new Date(d).toLocaleString('es-ES') : '—';

const STATUS_BADGE = {
  pending: 'badge-warning',
  done: 'badge-success',
  failed: 'badge-danger',
  canceled: 'badge-gray',
};

const ACTION_LABEL = {
  send_email:   'Enviar email',
  minerva_call: 'Llamada Minerva',
  check_sms:    'Comprobar SMS de cliente',
};

const ACTION_ICON = {
  send_email:   Mail,
  minerva_call: PhoneOutgoing,
  check_sms:    Mail,
};

const RBC_MESSAGES = {
  date: 'Fecha', time: 'Hora', event: 'Evento', allDay: 'Todo el día',
  week: 'Semana', work_week: 'Semana laboral', day: 'Día', month: 'Mes',
  previous: '←', next: '→', yesterday: 'Ayer', tomorrow: 'Mañana', today: 'Hoy',
  agenda: 'Agenda', noEventsInRange: 'No hay eventos en este rango',
  showMore: (n) => `+${n} más`,
};

// Presets de recurrencia. El detalle real (intervalo, días, hasta) se
// rellena dinámicamente a partir de `runAt` y de los campos opcionales
// (frequency, until). Si el usuario elige "custom" pega su propia rrule.
const RECURRENCE_PRESETS = [
  { value: '',         label: 'No se repite' },
  { value: 'daily',    label: 'Cada día' },
  { value: 'weekly',   label: 'Cada semana' },
  { value: 'monthly',  label: 'Cada mes' },
  { value: 'yearly',   label: 'Cada año' },
  { value: 'custom',   label: 'Personalizado (RRULE)' },
];

// Días de la semana de RRULE (lunes=MO ... domingo=SU) según orden JS
// Date#getDay (0=domingo).
const RRULE_WEEKDAY = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA];

function isLastDayOfMonth(d) {
  const next = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
  return next.getMonth() !== d.getMonth();
}

function buildRrule({ preset, runAt, until, customRrule }) {
  if (!preset) return null;
  if (preset === 'custom') return (customRrule || '').trim() || null;
  const start = new Date(runAt);
  const opts = { dtstart: start };
  if (preset === 'daily') {
    opts.freq = RRule.DAILY;
  } else if (preset === 'weekly') {
    opts.freq = RRule.WEEKLY;
    opts.byweekday = RRULE_WEEKDAY[start.getDay()];
  } else if (preset === 'monthly') {
    opts.freq = RRule.MONTHLY;
    // Si el día elegido es el último del mes, fija BYMONTHDAY=-1 para
    // que los meses con menos días caigan en SU último día (no en el
    // 1 del siguiente ni saltando a meses con esa cifra exacta).
    opts.bymonthday = isLastDayOfMonth(start) ? -1 : start.getDate();
  } else if (preset === 'yearly') {
    opts.freq = RRule.YEARLY;
    opts.bymonth = start.getMonth() + 1;
    opts.bymonthday = isLastDayOfMonth(start) ? -1 : start.getDate();
  }
  if (until) {
    const u = new Date(until);
    u.setHours(23, 59, 59, 0);
    opts.until = u;
  }
  return new RRule(opts).toString();
}

// Las repeticiones se dibujan en hora local, con el mismo cálculo con el que
// el CRM las envía (server/services/calendarWorker.js): rrule calcula en UTC y
// un aviso de las 09:00 puesto en invierno saldría a las 10:00 en verano. Se
// pasa la regla a «hora flotante» (la hora local escrita como si fuera UTC),
// se calcula ahí y el resultado vuelve a ser un instante real.
const toFloating = (d) => new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(),
  d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds()));
const fromFloating = (f) => new Date(f.getUTCFullYear(), f.getUTCMonth(), f.getUTCDate(),
  f.getUTCHours(), f.getUTCMinutes(), f.getUTCSeconds(), f.getUTCMilliseconds());

function occurrencesBetween(rruleStr, runAt, start, end) {
  const rule = rrulestr(rruleStr);
  // Reglas compuestas o con su propia zona (TZID): tal cual, como siempre.
  if (rule instanceof RRuleSet || rule.origOptions.tzid) return rule.between(start, end, true);
  const o = rule.origOptions;
  const local = new RRule({
    ...o,
    dtstart: toFloating(o.dtstart || new Date(runAt)),
    until: o.until ? toFloating(o.until) : null,
  });
  return local.between(toFloating(start), toFloating(end), true).map(fromFloating);
}

function detectPresetFromRrule(rrule) {
  if (!rrule) return { preset: '', until: '', customRrule: '' };
  try {
    const rule = rrulestr(rrule);
    const o = rule.origOptions || rule.options || {};
    const u = o.until ? new Date(o.until) : null;
    const untilStr = u ? u.toISOString().slice(0, 10) : '';
    const interval = o.interval ?? 1;
    // Patrones que generamos por preset (tras la mejora de "último día"
    // y BYDAY explícito): consideramos preset si TODAS las opciones
    // coinciden con lo que produciría buildRrule para ese preset.
    if (interval === 1) {
      if (o.freq === RRule.DAILY && !o.byweekday && !o.bymonth && !o.bymonthday) {
        return { preset: 'daily', until: untilStr, customRrule: rrule };
      }
      if (o.freq === RRule.WEEKLY && o.byweekday && (!o.bymonth) && (!o.bymonthday)) {
        return { preset: 'weekly', until: untilStr, customRrule: rrule };
      }
      if (o.freq === RRule.MONTHLY && !o.byweekday && !o.bymonth && o.bymonthday != null) {
        return { preset: 'monthly', until: untilStr, customRrule: rrule };
      }
      if (o.freq === RRule.YEARLY && !o.byweekday && o.bymonth != null && o.bymonthday != null) {
        return { preset: 'yearly', until: untilStr, customRrule: rrule };
      }
    }
    // Compatibilidad hacia atrás con la versión anterior (sin
    // BYMONTHDAY/BYDAY explícitos).
    if (interval === 1 && (!o.byweekday || o.byweekday.length === 0)
        && !o.bymonth && !o.bymonthday && !o.bysetpos) {
      if (o.freq === RRule.DAILY)   return { preset: 'daily',   until: untilStr, customRrule: rrule };
      if (o.freq === RRule.WEEKLY)  return { preset: 'weekly',  until: untilStr, customRrule: rrule };
      if (o.freq === RRule.MONTHLY) return { preset: 'monthly', until: untilStr, customRrule: rrule };
      if (o.freq === RRule.YEARLY)  return { preset: 'yearly',  until: untilStr, customRrule: rrule };
    }
    return { preset: 'custom', until: untilStr, customRrule: rrule };
  } catch {
    return { preset: 'custom', until: '', customRrule: rrule };
  }
}

const emptyForm = () => ({
  title: '',
  description: '',
  runAt: '',
  actionType: 'send_email',
  customerId: '',
  email: { to: '', cc: '', bcc: '', subject: '', body: '' },
  call:  { to_number: '', line_type: 'principal', recipient_name: '', message_recipient_name: '', message: '' },
  sms:   { recipient_email: '', period: 'this_month' },
  recurrence: { preset: '', until: '', customRrule: '' },
});

const localToInputValue = (date) => {
  const tzOffsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffsetMs).toISOString().slice(0, 16);
};

const Calendar = () => {
  const modal = useModal();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [lastRunByEvent, setLastRunByEvent] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [view, setView] = useUserPreference('calendar:view', Views.WEEK);
  const [date, setDate] = useUserPreference('calendar:date', new Date().toISOString());
  const [tab, setTab] = useUserPreference('calendar:tab', 'calendar'); // 'calendar' | 'list'
  const [search, setSearch] = useUserPreference('calendar:search', '');
  const [statusFilter, setStatusFilter] = useUserPreference('calendar:status', '');
  const [actionFilter, setActionFilter] = useUserPreference('calendar:action', '');
  const [viewMode, setViewMode] = useViewMode('calendar-list');
  const fl = useFieldLayout('calendar-list', viewMode);

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [historyOpen, setHistoryOpen] = useState(null); // { event, runs }
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => { load(); loadCustomers(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/calendar-events?limit=500');
      const events = Array.isArray(res.data) ? res.data : [];
      setItems(events);
      // Pre-cargar último run de cada evento check_sms para mostrar el saldo en la card.
      const checks = events.filter((e) => e.actionType === 'check_sms');
      const map = new Map();
      await Promise.all(checks.map(async (ev) => {
        try {
          const r = await api.get(`/calendar-events/${ev.id}/runs`);
          const runs = Array.isArray(r.data) ? r.data : [];
          const last = runs.find((x) => x.status === 'done' && x.summary) || runs[0] || null;
          if (last) map.set(ev.id, last);
        } catch { /* ignorar */ }
      }));
      setLastRunByEvent(map);
    } catch (e) {
      modal.error('Error cargando calendario');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const res = await api.get('/customers');
      setCustomers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // Silencioso: el calendario funciona sin clientes; queda solo sin
      // sugerencias en los campos.
    }
  };

  const customerById = useMemo(() => {
    const m = new Map();
    for (const c of customers) m.set(c.id, c);
    return m;
  }, [customers]);

  // Sugerencias de email para el datalist: el del usuario actual + los
  // de todos los clientes con email no vacío. Sin duplicados.
  const emailSuggestions = useMemo(() => {
    const out = [];
    const seen = new Set();
    if (user?.email) {
      out.push({ email: user.email, label: `${user.name || 'Yo'} (yo)` });
      seen.add(user.email.toLowerCase());
    }
    for (const c of customers) {
      const e = (c.email || '').trim();
      if (!e) continue;
      const k = e.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ email: e, label: c.name });
    }
    return out;
  }, [customers, user]);

  // Expande las rrules dentro del rango visible para mostrar todas las
  // ocurrencias en la rejilla del calendario (sin guardarlas en BD).
  const visibleRange = useMemo(() => {
    const ref = new Date(date);
    const start = new Date(ref); start.setMonth(start.getMonth() - 2); start.setDate(1); start.setHours(0,0,0,0);
    const end = new Date(ref);   end.setMonth(end.getMonth() + 6);     end.setDate(1); end.setHours(0,0,0,0);
    return { start, end };
  }, [date]);

  const calendarEvents = useMemo(() => {
    const out = [];
    for (const ev of items) {
      if (actionFilter && ev.actionType !== actionFilter) continue;
      const baseTitle = ev.title || '(sin título)';
      if (!ev.rrule) {
        const t = new Date(ev.runAt);
        out.push({ id: ev.id, raw: ev, title: baseTitle, start: t, end: new Date(t.getTime() + 30 * 60 * 1000), allDay: false });
      } else {
        try {
          const dates = occurrencesBetween(ev.rrule, ev.runAt, visibleRange.start, visibleRange.end);
          for (const d of dates) {
            out.push({ id: `${ev.id}-${d.getTime()}`, raw: ev, occurrenceAt: d, title: `🔁 ${baseTitle}`, start: d, end: new Date(d.getTime() + 30 * 60 * 1000), allDay: false });
          }
        } catch { /* ignorar evento con rrule corrupta */ }
      }
    }
    return out;
  }, [items, actionFilter, visibleRange]);

  const openNew = (slot) => {
    setEditingId(null);
    const f = emptyForm();
    if (slot?.start) f.runAt = localToInputValue(new Date(slot.start));
    setForm(f);
    setShowModal(true);
  };

  const openEdit = (ev) => {
    const f = emptyForm();
    f.title = ev.title || '';
    f.description = ev.description || '';
    f.runAt = localToInputValue(new Date(ev.runAt));
    f.actionType = ev.actionType;
    f.customerId = ev.payload?.customerId ? String(ev.payload.customerId) : '';
    if (ev.actionType === 'send_email') f.email = { ...f.email, ...(ev.payload || {}) };
    if (ev.actionType === 'minerva_call') f.call  = { ...f.call,  ...(ev.payload || {}) };
    if (ev.actionType === 'check_sms')    f.sms   = { ...f.sms,   ...(ev.payload || {}) };
    f.recurrence = detectPresetFromRrule(ev.rrule);
    setEditingId(ev.id);
    setForm(f);
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingId(null); };

  const handleSelectEvent = (rbcEv) => openEdit(rbcEv.raw);

  const buildPayloadAndValidate = () => {
    if (!form.title.trim()) throw new Error('Falta el título');
    if (!form.runAt) throw new Error('Falta la fecha y hora');

    let payload;
    if (form.actionType === 'send_email') {
      const { to, cc, bcc, subject, body } = form.email;
      if (!to.trim()) throw new Error('Email: falta destinatario');
      if (!subject.trim()) throw new Error('Email: falta asunto');
      if (!body.trim()) throw new Error('Email: falta cuerpo');
      payload = { to: to.trim(), subject: subject.trim(), body, ...(cc.trim() ? { cc: cc.trim() } : {}), ...(bcc.trim() ? { bcc: bcc.trim() } : {}) };
    } else if (form.actionType === 'minerva_call') {
      const { to_number, line_type, recipient_name, message_recipient_name, message } = form.call;
      if (!/^\+?[0-9]{6,20}$/.test(to_number.trim())) throw new Error('Llamada: número inválido');
      if (!line_type.trim()) throw new Error('Llamada: falta tipo de línea');
      if (!message.trim()) throw new Error('Llamada: falta mensaje');
      payload = { to_number: to_number.trim(), line_type: line_type.trim(), message,
        ...(recipient_name.trim() ? { recipient_name: recipient_name.trim() } : {}),
        ...(message_recipient_name.trim() ? { message_recipient_name: message_recipient_name.trim() } : {}) };
    } else if (form.actionType === 'check_sms') {
      const { recipient_email, period } = form.sms;
      if (!recipient_email.trim() || !/.+@.+\..+/.test(recipient_email.trim())) throw new Error('SMS: email destino inválido');
      if (!form.customerId) throw new Error('SMS: selecciona el cliente cuyo appcrm hay que comprobar');
      payload = { recipient_email: recipient_email.trim(), period: period || 'this_month' };
    }
    if (form.customerId) {
      payload.customerId = parseInt(form.customerId, 10);
    }

    const rrule = buildRrule({
      preset: form.recurrence.preset,
      runAt: form.runAt,
      until: form.recurrence.until,
      customRrule: form.recurrence.customRrule,
    });
    if (form.recurrence.preset === 'custom' && !rrule) throw new Error('Recurrencia personalizada vacía');

    return {
      title: form.title.trim(),
      description: form.description,
      runAt: new Date(form.runAt).toISOString(),
      actionType: form.actionType,
      payload,
      rrule,
    };
  };

  const persist = async (data) => {
    setSaving(true);
    try {
      if (editingId) await api.put(`/calendar-events/${editingId}`, data);
      else           await api.post('/calendar-events', data);
      await load();
      closeModal();
    } catch (err) {
      modal.error(err.response?.data?.message || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    let data;
    try { data = buildPayloadAndValidate(); }
    catch (err) { modal.error(err.message); return; }

    if (data.actionType === 'minerva_call' && data.rrule) {
      const presetLabel = RECURRENCE_PRESETS.find((p) => p.value === form.recurrence.preset)?.label || 'recurrente';
      modal.confirm(
        `Vas a programar una LLAMADA RECURRENTE de Minerva a ${data.payload.to_number} (${presetLabel.toLowerCase()}).\n\nMinerva llamará automáticamente cada vez que toque y leerá el mensaje. ¿Confirmas?`,
        () => persist(data),
      );
      return;
    }

    persist(data);
  };

  const remove = (id) => {
    modal.confirm('¿Borrar este evento? Si es recurrente se borra toda la serie y su historial.', async () => {
      try {
        await api.delete(`/calendar-events/${id}`);
        await load();
      } catch (e) {
        modal.error('Error borrando');
      }
    });
  };

  const runNow = (id) => {
    modal.confirm('¿Ejecutar este evento ahora?', async () => {
      try {
        await api.post(`/calendar-events/${id}/run-now`);
        await load();
      } catch (err) {
        modal.error(err.response?.data?.message || 'Error ejecutando');
      }
    });
  };

  const openHistory = async (ev) => {
    setHistoryOpen({ event: ev, runs: [] });
    setHistoryLoading(true);
    try {
      const res = await api.get(`/calendar-events/${ev.id}/runs`);
      setHistoryOpen({ event: ev, runs: Array.isArray(res.data) ? res.data : [] });
    } catch (e) {
      modal.error('Error cargando historial');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Drag & drop: solo eventos NO recurrentes (mover una sola ocurrencia
  // de una serie es decisión más compleja que dejamos fuera de alcance).
  const handleEventDrop = async ({ event, start }) => {
    const raw = event.raw;
    if (raw.rrule) {
      modal.info('Para mover un evento recurrente, edítalo y cambia la regla de recurrencia.');
      return;
    }
    try {
      await api.put(`/calendar-events/${raw.id}`, { runAt: new Date(start).toISOString() });
      await load();
    } catch (e) {
      modal.error(e.response?.data?.message || 'Error moviendo el evento');
    }
  };

  const filteredList = items.filter((ev) => {
    const q = search.toLowerCase();
    const payloadStr = JSON.stringify(ev.payload || {}).toLowerCase();
    const matchSearch = !q ||
      (ev.title || '').toLowerCase().includes(q) ||
      (ev.description || '').toLowerCase().includes(q) ||
      payloadStr.includes(q);
    const matchStatus = !statusFilter || ev.status === statusFilter;
    const matchAction = !actionFilter || ev.actionType === actionFilter;
    return matchSearch && matchStatus && matchAction;
  });

  const eventPropGetter = (event) => {
    const ev = event.raw;
    const kind = ev.actionType === 'minerva_call' ? 'call' : 'email';
    const stateForOccurrence = event.occurrenceAt ? 'pending' : (ev.status || 'pending');
    const classes = [`crm-ev-${kind}-${stateForOccurrence}`];
    if (ev.rrule) classes.push('crm-ev-recurring');
    return { className: classes.join(' ') };
  };

  // Festivos nacionales España (fechas fijas). Se actualiza año a año.
  // Si en el futuro hace falta festivos autonómicos/locales, mover a setting.
  const NATIONAL_HOLIDAYS = useMemo(() => new Set([
    // 2026
    '2026-01-01', '2026-01-06', '2026-04-03', '2026-05-01',
    '2026-08-15', '2026-10-12', '2026-11-01', '2026-12-06',
    '2026-12-08', '2026-12-25',
    // 2027 (para que no desaparezca al pasar de año)
    '2027-01-01', '2027-01-06', '2027-03-26', '2027-05-01',
    '2027-08-15', '2027-10-12', '2027-11-01', '2027-12-06',
    '2027-12-08', '2027-12-25',
  ]), []);

  const dayPropGetter = (d) => {
    const classes = [];
    const dow = d.getDay();
    if (dow === 0 || dow === 6) classes.push('rbc-weekend');
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (NATIONAL_HOLIDAYS.has(key)) classes.push('rbc-holiday');
    return classes.length > 0 ? { className: classes.join(' ') } : {};
  };

  // Mapa fecha-clave → array de horas con eventos, para dibujar la
  // mini-regla horaria en cada celda del mes.
  const hoursByDay = useMemo(() => {
    const map = new Map();
    for (const ev of calendarEvents) {
      const s = ev.start instanceof Date ? ev.start : new Date(ev.start);
      const key = `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}-${String(s.getDate()).padStart(2, '0')}`;
      const hour = s.getHours();
      const kind = ev.raw?.actionType === 'minerva_call' ? 'call' : 'email';
      const list = map.get(key) || [];
      list.push({ hour, kind });
      map.set(key, list);
    }
    return map;
  }, [calendarEvents]);

  const DateCellWrapper = ({ value, children }) => {
    const key = `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
    const events = hoursByDay.get(key) || [];
    if (events.length === 0) return children;
    const ticks = Array.from({ length: 24 }).map((_, h) => {
      const hit = events.find((e) => e.hour === h);
      const cls = hit ? `crm-day-tick on crm-day-tick-${hit.kind}` : 'crm-day-tick';
      return <span key={h} className={cls} />;
    });
    const ruleNode = <div className="crm-day-rule">{ticks}</div>;
    return React.cloneElement(children, {
      children: (
        <>
          {children.props.children}
          {ruleNode}
        </>
      ),
    });
  };

  // Coloreado sutil de franjas: madrugada / mañana / mediodía / tarde / noche.
  const slotPropGetter = (d) => {
    const h = d.getHours();
    if (h < 6)  return { className: 'rbc-slot-night' };
    if (h < 12) return { className: 'rbc-slot-morning' };
    if (h < 14) return { className: 'rbc-slot-noon' };
    if (h < 20) return { className: 'rbc-slot-afternoon' };
    return { className: 'rbc-slot-evening' };
  };

  // En vista MES los eventos se renderizan pequeñitos. Le metemos la hora
  // delante para que se vea de un vistazo cuándo es.
  const monthEventComponent = ({ event }) => {
    const t = event.start instanceof Date ? event.start : new Date(event.start);
    const hh = String(t.getHours()).padStart(2, '0');
    const mm = String(t.getMinutes()).padStart(2, '0');
    return (
      <span>
        <strong style={{ marginRight: 6 }}>{hh}:{mm}</strong>{event.title}
      </span>
    );
  };

  const calendarComponents = {
    month: { event: monthEventComponent },
    dateCellWrapper: DateCellWrapper,
  };

  // Columnas de la vista LISTA en modo tabla. Cada columna refleja un dato
  // que ya muestra la tarjeta de la lista; reutiliza los mismos handlers.
  const columns = [
    { key: 'title', header: 'Título', className: 'dt-strong', fixed: true, render: (ev) => (
      <>
        {ev.title}
        {ev.rrule && <Repeat size={14} style={{ marginLeft: 6, color: '#64748b' }} />}
      </>
    ) },
    { key: 'runAt', header: 'Fecha y hora', className: 'dt-nowrap', render: (ev) => formatDateLong(ev.runAt) },
    { key: 'actionType', header: 'Acción', render: (ev) => ACTION_LABEL[ev.actionType] || ev.actionType },
    { key: 'detail', header: 'Detalle', render: (ev) => (
      ev.actionType === 'send_email' ? (ev.payload?.to || '—')
        : ev.actionType === 'minerva_call' ? (ev.payload?.to_number || '—')
        : ev.actionType === 'check_sms' ? (ev.payload?.recipient_email || '—')
        : '—'
    ) },
    { key: 'customer', header: 'Cliente', render: (ev) => {
      const c = ev.payload?.customerId ? customerById.get(ev.payload.customerId) : null;
      return c ? c.name : '—';
    } },
    { key: 'status', header: 'Estado', render: (ev) => (
      <span className={`badge ${STATUS_BADGE[ev.status] || 'badge-gray'}`}>{ev.status}</span>
    ) },
    { key: 'attempts', header: 'Intentos', render: (ev) => `${ev.attempts}/${ev.maxAttempts}` },
    { key: 'actions', header: '', className: 'col-actions', render: (ev) => (
      <div className="dt-actions">
        {ev.status === 'pending' && (
          <button className="btn-icon" onClick={() => runNow(ev.id)} title="Ejecutar ahora" style={{ color: '#15803d' }}>
            <Play size={16} />
          </button>
        )}
        <button className="btn-icon" onClick={() => openHistory(ev)} title="Historial"><History size={16} /></button>
        <button className="btn btn-sm btn-outline" onClick={() => openEdit(ev)}>Editar</button>
        <button className="btn-icon" onClick={() => remove(ev.id)} title="Borrar" style={{ color: '#ef4444' }}>
          <Trash2 size={16} />
        </button>
      </div>
    ) },
  ];

  const fields = fl.fields(columns);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Calendario</h1>
          <p className="page-subtitle">{items.length} eventos programados</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div className="btn-group" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <button type="button" className={`btn btn-sm ${tab === 'calendar' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab('calendar')}><CalendarDays size={16} /> Calendario</button>
            <button type="button" className={`btn btn-sm ${tab === 'list' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab('list')}><ListIcon size={16} /> Lista</button>
          </div>
          <button className="btn btn-primary" onClick={() => openNew()}>
            <Plus size={18} /> Nuevo evento
          </button>
        </div>
      </div>

      {tab === 'calendar' && (
        <div className="card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <select className="form-input" style={{ width: 200 }} value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
              <option value="">Todas las acciones</option>
              <option value="send_email">Enviar email</option>
              <option value="minerva_call">Llamada Minerva</option>
            </select>
          </div>
          <div style={{ height: 'calc(100vh - 300px)', minHeight: 560 }}>
            <DnDCalendar
              className="crmllor-calendar"
              localizer={localizer}
              culture="es"
              messages={RBC_MESSAGES}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              view={view}
              onView={setView}
              date={new Date(date)}
              onNavigate={(d) => setDate(d.toISOString())}
              views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
              defaultView={Views.WEEK}
              step={30}
              timeslots={2}
              min={new Date(1970, 0, 1, 6, 0, 0)}
              max={new Date(1970, 0, 1, 23, 0, 0)}
              selectable
              onSelectSlot={openNew}
              onSelectEvent={handleSelectEvent}
              onEventDrop={handleEventDrop}
              draggableAccessor={(ev) => !ev.raw?.rrule}
              resizable={false}
              eventPropGetter={eventPropGetter}
              dayPropGetter={dayPropGetter}
              slotPropGetter={slotPropGetter}
              components={calendarComponents}
              popup
            />
          </div>
          <div className="calendar-legend">
            <span className="legend-dot legend-email">Email pendiente</span>
            <span className="legend-dot legend-call">Llamada pendiente</span>
            <span className="legend-dot legend-failed">Fallido</span>
            <span className="legend-dot legend-done">Completado</span>
            <span className="legend-dot legend-recurring">Se repite</span>
          </div>
        </div>
      )}

      {tab === 'list' && (
        <div className="card">
          <div className="search-bar">
            <div className="search-input-wrapper">
              <Search />
              <input className="form-input" placeholder="Buscar por título, descripción o contenido..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select className="form-input" style={{ width: 180 }} value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
              <option value="">Todas las acciones</option>
              <option value="send_email">Enviar email</option>
              <option value="minerva_call">Llamada Minerva</option>
            </select>
            <select className="form-input" style={{ width: 200 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Todos los estados</option>
              <option value="pending">Pendientes</option>
              <option value="done">Completados</option>
              <option value="failed">Fallidos</option>
              <option value="canceled">Cancelados</option>
            </select>
            <div style={{ marginLeft: 'auto' }}>
              <VistaControles
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                fields={fields}
                hidden={fl.hidden}
                onToggleField={fl.toggle}
                onReorderFields={fl.reorder}
              />
            </div>
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
          ) : filteredList.length === 0 ? (
            <div className="empty-state"><CalendarDays size={64} /><h3>Sin eventos</h3></div>
          ) : viewMode === 'table' ? (
            <DataTable hiddenKeys={fl.hidden} columns={fl.orderColumns(columns)} rows={filteredList} />
          ) : (
            <div className="items-grid">
              {filteredList.map((ev) => {
                const ActionIcon = ACTION_ICON[ev.actionType] || CalendarDays;
                return (
                  <div className="item-card" key={ev.id}>
                    <div className="item-card-header">
                      <div className="item-card-avatar"><ActionIcon size={20} /></div>
                      <div style={{ flex: 1 }}>
                        <div className="item-card-title">
                          {ev.title}
                          {ev.rrule && <Repeat size={14} style={{ marginLeft: 6, color: '#64748b' }} />}
                        </div>
                        {!fl.isHidden('runAt') && <div className="item-card-subtitle">{formatDateLong(ev.runAt)}</div>}
                      </div>
                      {!fl.isHidden('status') && <span className={`badge ${STATUS_BADGE[ev.status] || 'badge-gray'}`}>{ev.status}</span>}
                    </div>
                    <div className="item-card-body">
                      {!fl.isHidden('actionType') && <div className="item-card-row" style={fl.cardStyle('actionType')}><span className="item-label">Acción</span><span>{ACTION_LABEL[ev.actionType] || ev.actionType}</span></div>}
                      {!fl.isHidden('customer') && ev.payload?.customerId && customerById.get(ev.payload.customerId) && (
                        <div className="item-card-row" style={fl.cardStyle('customer')}><span className="item-label">Cliente</span><span>{customerById.get(ev.payload.customerId).name}</span></div>
                      )}
                      {ev.actionType === 'send_email' && (
                        <>
                          {!fl.isHidden('detail') && <div className="item-card-row" style={fl.cardStyle('detail')}><span className="item-label">Para</span><span>{ev.payload?.to}</span></div>}
                          <div className="item-card-row"><span className="item-label">Asunto</span><span>{ev.payload?.subject}</span></div>
                        </>
                      )}
                      {ev.actionType === 'minerva_call' && (
                        <>
                          {!fl.isHidden('detail') && <div className="item-card-row" style={fl.cardStyle('detail')}><span className="item-label">Llamar a</span><span>{ev.payload?.to_number}</span></div>}
                          {ev.payload?.recipient_name && <div className="item-card-row"><span className="item-label">Destinatario</span><span>{ev.payload.recipient_name}</span></div>}
                        </>
                      )}
                      {ev.actionType === 'check_sms' && (
                        <>
                          {!fl.isHidden('detail') && <div className="item-card-row" style={fl.cardStyle('detail')}><span className="item-label">Email destino</span><span>{ev.payload?.recipient_email}</span></div>}
                          <div className="item-card-row"><span className="item-label">Periodo</span><span>{ev.payload?.period || 'all'}</span></div>
                          {(() => {
                            const run = lastRunByEvent.get(ev.id);
                            if (!run || !run.summary) return <div className="item-card-row"><span className="item-label">Saldo</span><span style={{ color: '#94a3b8' }}>Sin ejecuciones todavía</span></div>;
                            const s = run.summary;
                            const saldo = Number(s.saldoEur ?? 0);
                            const color = saldo < 0 ? '#b91c1c' : saldo < 5 ? '#b45309' : '#15803d';
                            return (
                              <>
                                <div className="item-card-row"><span className="item-label">Último saldo</span><span style={{ color, fontWeight: 700 }}>{saldo.toFixed(2)} €</span></div>
                                <div className="item-card-row"><span className="item-label">Gastado / prepagado</span><span>{Number(s.gastadoEur || 0).toFixed(2)} € / {Number(s.prepagado || 0).toFixed(2)} €</span></div>
                                <div className="item-card-row"><span className="item-label">SMS último</span><span>{s.smsTotal} ({s.smsOk} ok / {s.smsError} err)</span></div>
                                <div className="item-card-row"><span className="item-label">Comprobado</span><span style={{ fontSize: 12, color: '#64748b' }}>{formatDateLong(run.createdAt)}</span></div>
                              </>
                            );
                          })()}
                        </>
                      )}
                      {ev.rrule && <div className="item-card-row"><span className="item-label">Recurrencia</span><span style={{ fontFamily: 'monospace', fontSize: 11 }}>{ev.rrule.replace('RRULE:', '')}</span></div>}
                      {!fl.isHidden('attempts') && <div className="item-card-row" style={fl.cardStyle('attempts')}><span className="item-label">Intentos</span><span>{ev.attempts}/{ev.maxAttempts}</span></div>}
                      {ev.lastError && (
                        <div className="item-card-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                          <span className="item-label">Último error</span>
                          <span style={{ fontSize: 12, color: '#b91c1c' }}>{ev.lastError}</span>
                        </div>
                      )}
                    </div>
                    <div className="item-card-footer">
                      <div className="item-card-actions" style={{ display: 'flex', gap: 8 }}>
                        {ev.status === 'pending' && (
                          <button className="btn-icon" onClick={() => runNow(ev.id)} title="Ejecutar ahora" style={{ color: '#15803d' }}>
                            <Play size={16} />
                          </button>
                        )}
                        <button className="btn-icon" onClick={() => openHistory(ev)} title="Historial"><History size={16} /></button>
                        <button className="btn btn-sm btn-outline" onClick={() => openEdit(ev)}>Editar</button>
                        <button className="btn-icon" onClick={() => remove(ev.id)} title="Borrar" style={{ color: '#ef4444' }}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Editar evento' : 'Nuevo evento'}</h2>
              <button className="btn-icon" onClick={closeModal}><XIcon size={20} /></button>
            </div>
            <form onSubmit={save}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label className="form-label">Título</label>
                  <input className="form-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">Fecha y hora (primera vez)</label>
                    <input type="datetime-local" className="form-input" value={form.runAt} onChange={(e) => setForm({ ...form, runAt: e.target.value })} />
                  </div>
                  <div style={{ width: 240 }}>
                    <label className="form-label">Acción</label>
                    <select className="form-input" value={form.actionType} onChange={(e) => setForm({ ...form, actionType: e.target.value })}>
                      <option value="send_email">Enviar email</option>
                      <option value="minerva_call">Llamada Minerva</option>
                      <option value="check_sms">Comprobar SMS de cliente</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="form-label">Cliente relacionado (opcional)</label>
                  <select
                    className="form-input"
                    value={form.customerId}
                    onChange={(e) => {
                      const cid = e.target.value;
                      const c = cid ? customerById.get(parseInt(cid, 10)) : null;
                      setForm((prev) => {
                        const next = { ...prev, customerId: cid };
                        // Autorrellenar destinatario según acción si la
                        // ficha tiene el dato. No sobreescribe si el
                        // usuario ya escribió algo.
                        if (c) {
                          if (next.actionType === 'send_email' && c.email && !next.email.to.trim()) {
                            next.email = { ...next.email, to: c.email };
                          }
                          if (next.actionType === 'minerva_call') {
                            const phone = (c.mobile || c.phone || '').trim();
                            if (phone && !next.call.to_number.trim()) {
                              next.call = { ...next.call, to_number: phone, recipient_name: next.call.recipient_name || c.name || '' };
                            }
                          }
                        }
                        return next;
                      });
                    }}
                  >
                    <option value="">— Ninguno —</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                <fieldset style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
                  <legend style={{ fontWeight: 600, padding: '0 6px' }}>Repetición</legend>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <label className="form-label">¿Se repite?</label>
                      <select className="form-input" value={form.recurrence.preset} onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, preset: e.target.value } })}>
                        {RECURRENCE_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </div>
                    {form.recurrence.preset && form.recurrence.preset !== 'custom' && (
                      <div style={{ width: 200 }}>
                        <label className="form-label">Repetir hasta (opcional)</label>
                        <input type="date" className="form-input" value={form.recurrence.until} onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, until: e.target.value } })} />
                      </div>
                    )}
                    {form.recurrence.preset === 'custom' && (
                      <div style={{ flexBasis: '100%' }}>
                        <label className="form-label">RRULE personalizada (formato iCal RFC 5545)</label>
                        <input className="form-input" placeholder="FREQ=WEEKLY;BYDAY=MO,WE,FR;UNTIL=20260801T235959Z" value={form.recurrence.customRrule} onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, customRrule: e.target.value } })} />
                      </div>
                    )}
                  </div>
                </fieldset>

                <div>
                  <label className="form-label">Notas (uso interno)</label>
                  <textarea className="form-input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>

                {form.actionType === 'check_sms' && (
                  <fieldset style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
                    <legend style={{ fontWeight: 600, padding: '0 6px' }}>Comprobar SMS</legend>
                    <datalist id="calendar-email-suggestions">
                      {emailSuggestions.map((s) => (
                        <option key={s.email} value={s.email}>{s.label}</option>
                      ))}
                    </datalist>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
                        Consulta el endpoint de appcrm del cliente seleccionado arriba y envía un email con SMS enviados, créditos consumidos, prepagado, gastado y saldo restante (alerta si &lt; 5 €).
                      </p>
                      <div>
                        <label className="form-label">Email destino del informe</label>
                        <input className="form-input" list="calendar-email-suggestions" placeholder="Tu email u otro" value={form.sms.recipient_email} onChange={(e) => setForm({ ...form, sms: { ...form.sms, recipient_email: e.target.value } })} />
                      </div>
                      <div style={{ width: 280 }}>
                        <label className="form-label">Periodo</label>
                        <select className="form-input" value={form.sms.period} onChange={(e) => setForm({ ...form, sms: { ...form.sms, period: e.target.value } })}>
                          <option value="this_month">Mes en curso</option>
                          <option value="last_30_days">Últimos 30 días</option>
                          <option value="all">Histórico completo</option>
                        </select>
                      </div>
                    </div>
                  </fieldset>
                )}
                {form.actionType === 'send_email' && (
                  <fieldset style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
                    <legend style={{ fontWeight: 600, padding: '0 6px' }}>Email</legend>
                    <datalist id="calendar-email-suggestions">
                      {emailSuggestions.map((s) => (
                        <option key={s.email} value={s.email}>{s.label}</option>
                      ))}
                    </datalist>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div>
                        <label className="form-label">Para</label>
                        <input className="form-input" list="calendar-email-suggestions" placeholder="Escribe o elige" value={form.email.to} onChange={(e) => setForm({ ...form, email: { ...form.email, to: e.target.value } })} />
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ flex: 1 }}>
                          <label className="form-label">CC</label>
                          <input className="form-input" list="calendar-email-suggestions" placeholder="Escribe o elige" value={form.email.cc} onChange={(e) => setForm({ ...form, email: { ...form.email, cc: e.target.value } })} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label className="form-label">CCO</label>
                          <input className="form-input" list="calendar-email-suggestions" placeholder="Escribe o elige" value={form.email.bcc} onChange={(e) => setForm({ ...form, email: { ...form.email, bcc: e.target.value } })} />
                        </div>
                      </div>
                      <div>
                        <label className="form-label">Asunto</label>
                        <input className="form-input" value={form.email.subject} onChange={(e) => setForm({ ...form, email: { ...form.email, subject: e.target.value } })} />
                      </div>
                      <div>
                        <label className="form-label">Cuerpo</label>
                        <textarea className="form-input" rows={8} value={form.email.body} onChange={(e) => setForm({ ...form, email: { ...form.email, body: e.target.value } })} />
                      </div>
                    </div>
                  </fieldset>
                )}
                {form.actionType === 'minerva_call' && (
                  <fieldset style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
                    <legend style={{ fontWeight: 600, padding: '0 6px' }}>Llamada Minerva</legend>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ flex: 1 }}>
                          <label className="form-label">Llamar a (E.164)</label>
                          <input className="form-input" placeholder="+34..." value={form.call.to_number} onChange={(e) => setForm({ ...form, call: { ...form.call, to_number: e.target.value } })} />
                        </div>
                        <div style={{ width: 180 }}>
                          <label className="form-label">Línea</label>
                          <input className="form-input" value={form.call.line_type} onChange={(e) => setForm({ ...form, call: { ...form.call, line_type: e.target.value } })} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ flex: 1 }}>
                          <label className="form-label">Persona representada (opcional)</label>
                          <input className="form-input" placeholder="ej. Juan, jefe" value={form.call.recipient_name} onChange={(e) => setForm({ ...form, call: { ...form.call, recipient_name: e.target.value } })} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label className="form-label">Nombre del destinatario (opcional)</label>
                          <input className="form-input" placeholder="al que llama Minerva" value={form.call.message_recipient_name} onChange={(e) => setForm({ ...form, call: { ...form.call, message_recipient_name: e.target.value } })} />
                        </div>
                      </div>
                      <div>
                        <label className="form-label">Mensaje (Minerva lo contará con sus palabras pero respetando el contenido literal)</label>
                        <textarea className="form-input" rows={6} value={form.call.message} onChange={(e) => setForm({ ...form, call: { ...form.call, message: e.target.value } })} />
                      </div>
                    </div>
                  </fieldset>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Guardando...' : (editingId ? 'Guardar cambios' : 'Crear')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {historyOpen && (
        <div className="modal-overlay" onClick={() => setHistoryOpen(null)}>
          <div className="modal" style={{ maxWidth: 600 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Historial — {historyOpen.event.title}</h2>
              <button className="btn-icon" onClick={() => setHistoryOpen(null)}><XIcon size={20} /></button>
            </div>
            <div className="modal-body">
              {historyLoading ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
              ) : historyOpen.runs.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>Sin ejecuciones todavía.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {historyOpen.runs.map((r) => (
                    <div key={r.id} style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 8, borderBottom: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{formatDateLong(r.runAtActual)}</span>
                        <span className={`badge ${r.status === 'done' ? 'badge-success' : r.status === 'failed' ? 'badge-danger' : 'badge-warning'}`}>{r.status}</span>
                      </div>
                      {r.error && <span style={{ fontSize: 12, color: '#b91c1c' }}>{r.error}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline" onClick={() => setHistoryOpen(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
